import { BaseStorage } from "@/storage/BaseStorage";
import Bugsnag from "@bugsnag/js";
import { v4 as uuid } from "uuid";

import { TableOverview, TableStatus } from "@/services/Table";
import { TableItem } from "@/services/TableItem";

import { ClosingTableException } from "../expections/ClosingTableException";
import { SaiposTableConfiguration } from "@/core/SaiposTableConfiguration";
import { SaiposAPI } from "@/api/SaiposAPI";
import { SaiposOrder } from "./SaiposOrder";

const SaiposStatus = {
  /* Quando a mesa estiver fechada */
  [TableStatus.CLOSED]: 1,

  /* Quando a mesa estiver em consumo */
  [TableStatus.OPENED]: 2,

  /* Quando a mesa estiver em fechamento */
  [TableStatus.PRECLOSED]: 3,
};

const getStatusFromValue = (value) => {
  const keys = Object.entries(SaiposStatus);
  const key = keys.find(([key, val]) => val === value);
  return key ? key[0] : null;
};

export class SaiposTable {
  constructor() {
    this.saiposConfig = SaiposTableConfiguration.fromStorage();

    Bugsnag.leaveBreadcrumb(
      "Configurações da Saipos",
      this.saiposConfig.export(),
    );

    const tableNumber = this.saiposConfig.externalId;
    this.storage = new BaseStorage({ key: `of-saipos-table-${tableNumber}` });

    this.client = new SaiposAPI({
      storeId: this.saiposConfig.codStore,
      partnerId: this.saiposConfig.idPartner,
      baseURL: "/api/saipos",
    });

    this.orders = [];
  }

  async addItems(order) {
    const table = await this.getOverview();
    if (
      table.status === TableStatus.PRECLOSED ||
      table.status === TableStatus.CLOSED
    ) {
      throw new ClosingTableException();
    }

    const lastOrder = this.getLastOrder();

    const orderId = lastOrder?.posOrderId || uuid();

    const saiposOrder = new SaiposOrder({
      order,
      storeId: this.saiposConfig.codStore,
      orderId: orderId,
      tableId: this.saiposConfig.externalId,
    });

    const convertedOrder = saiposOrder.export();

    const createOrderResponse = await this.client.createOrder(convertedOrder);
    const errors = createOrderResponse.getErrors([]);
    if (errors.length > 0) {
      throw new Error("Não foi possível incluir os itens na mesa");
    }
  }

  async callWaiter() {
    throw new Error("Chamar garçom indisponível");
  }

  async preClose() {
    const table = await this.getOverview();

    if (table.status === TableStatus.CLOSED) {
      throw new Error("Mesa fechada");
    }

    if (table.status === TableStatus.PRECLOSED) {
      throw new Error("Mesa em fechamento");
    }

    const ordersResponse = await this.client.getTableById(
      this.saiposConfig.externalId,
    );
    const orders = ordersResponse.getData([]);

    const orderIds = orders
      .filter(
        (o) =>
          ![SaiposStatus.CLOSED, SaiposStatus.PRECLOSED].includes(
            o.id_table_order_status,
          ),
      )
      .map((o) => o.order_id);

    await Promise.all(
      orderIds.map(async (orderId) => {
        await this.client.closeTableByOrderId(orderId);
      }),
    );
  }

  getLastOrder() {
    if (this.orders.length === 0) {
      return null;
    }

    const openedOrders = this.orders.filter(
      (o) => o.id_table_order_status === SaiposStatus.OPENED,
    );
    if (openedOrders.length === 0) {
      return null;
    }

    const sortedOrders = openedOrders.sort((prevOrder, nextOrder) => {
      const prevOrderId = Number(prevOrder.order_id);
      const nextOrderId = Number(nextOrder.order_id);
      return nextOrderId - prevOrderId;
    });

    const [lastOrder] = sortedOrders;

    return {
      posOrderId: lastOrder.order_id,
    };
  }

  async convertPosOrderToApiOrder() {
    const convertResponse = await this.client.convertPosTable(
      this.saiposConfig.externalId,
    );
    if (convertResponse.getErrors([]).length > 0) {
      throw new Error(
        "Falha na conversão do pedido existente no POS" -
          convertResponse.getErrors()[0],
      );
    }

    const updatedTableResponse = await this.client.getTableById(
      this.saiposConfig.externalId,
    );

    return updatedTableResponse.getData([]);
  }

  async getOverview() {
    const tableResponse = await this.client.getTableById(
      this.saiposConfig.externalId,
    );

    let tableOrders = tableResponse.getData([]);
    this.orders = tableOrders || [];

    if (tableOrders.length === 0) {
      return new TableOverview({
        subtotal: 0,
        total: 0,
        fee: 0,
        status: TableStatus.AVAILABLE,
        items: [],
        payersQuantity: 1,
      });
    }

    const exclusivePosOrders = tableOrders.filter((o) => !o.order_id);
    const hasExclusivePosOrders = exclusivePosOrders.length > 0;
    if (hasExclusivePosOrders) {
      tableOrders = await this.convertPosOrderToApiOrder();
      this.orders = tableOrders || [];
    }

    const orderIds = tableOrders.map((o) => o.order_id).filter(Boolean);

    const orderResponses = await this.client.getOrderByIds(orderIds);
    const orders = orderResponses
      .filter((r) => r.isSuccess())
      .map((r) => r.getData({}));

    const statuses = new Set(
      tableOrders.map((o) => getStatusFromValue(o.id_table_order_status)),
    );

    let status = TableStatus.CLOSED;
    if (statuses.has(TableStatus.OPENED)) status = TableStatus.OPENED;
    if (statuses.has(TableStatus.PRECLOSED)) status = TableStatus.PRECLOSED;

    let subtotal = 0;
    let fee = 0;
    let total = 0;
    const allItems = [];

    orders.forEach((order) => {
      subtotal += order.totalItems || 0;
      fee += (order.totalItems || 0) * ((order.serviceFee || 0) / 100);
      total += order.totalValue || 0;

      const items = (order.items || [])
        .filter((item) => item.deleted === "N")
        .map(
          (item, index) =>
            new TableItem({
              id: item.integration_code,
              name: item.desc_sale_item,
              sort: index,
              quantity: item.quantity,
              unitPrice: item.unit_price,
              totalPrice: item.quantity * item.unit_price,
              items: (item.choice_items || [])
                .filter((c) => c.deleted === "N")
                .map((c, ci) => ({
                  id: c.integration_code,
                  name: c.desc_sale_item_choice,
                  sort: ci,
                  quantity: c.quantity || 1,
                  unitPrice: c.aditional_price || 0,
                  totalPrice: (c.quantity || 1) * (c.aditional_price || 0),
                })),
            }),
        );

      allItems.push(...items);
    });

    return new TableOverview({
      subtotal,
      total,
      fee,
      status,
      items: allItems,
      payersQuantity: 1,
    });
  }
}
